/**
 * Política de polling com backoff para acompanhar o status do job (R19.6/R19.8).
 *
 * O acompanhamento consulta `GET /jobs/{id}` periodicamente até um estado
 * terminal. Para ser gentil com a API e resiliente a instabilidades, o intervalo
 * cresce a cada consulta (backoff) até um teto, e erros de rede transitórios não
 * derrubam o acompanhamento imediatamente — são tolerados até um número máximo
 * de falhas consecutivas.
 */

export interface BackoffConfig {
  /** Intervalo inicial entre consultas, em ms. */
  readonly initialMs: number;
  /** Intervalo máximo entre consultas, em ms. */
  readonly maxMs: number;
  /** Fator multiplicativo aplicado a cada passo. */
  readonly factor: number;
}

export const DEFAULT_BACKOFF: BackoffConfig = {
  initialMs: 2000,
  maxMs: 15000,
  factor: 1.5,
};

/** Nº de falhas de rede consecutivas toleradas antes de reportar erro. */
export const MAX_CONSECUTIVE_FAILURES = 4;

/**
 * Calcula o atraso (ms) antes da próxima consulta, dado o nº de consultas já
 * feitas (`attempt`, começando em 0). Cresce geometricamente até `maxMs`.
 */
export function computeBackoffDelay(attempt: number, config: BackoffConfig = DEFAULT_BACKOFF): number {
  if (attempt <= 0) {
    return config.initialMs;
  }
  const raw = config.initialMs * Math.pow(config.factor, attempt);
  return Math.min(Math.round(raw), config.maxMs);
}
